import React, { useState } from 'react';
import {
  Button, Form, Input, Select, Alert, Typography, Collapse, Avatar,
} from 'antd';
import {
  RocketOutlined, KeyOutlined, UnorderedListOutlined, CheckCircleOutlined,
  ArrowRightOutlined, ArrowLeftOutlined, MailOutlined,
  QuestionCircleOutlined, SafetyOutlined, GlobalOutlined, UserOutlined,
  LinkOutlined, InfoCircleOutlined, BankOutlined, EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Text, Paragraph } = Typography;
const { Option } = Select;

/* ── Helpers ── */
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const getJwtPayload = () => {
  try {
    const token   = localStorage.getItem('token');
    const payload = token.split('.')[1];
    const padded  = payload + '='.repeat((4 - payload.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch { return {}; }
};

/* ── Step indicator ── */
const STEPS = [
  { title: 'Welcome',     icon: <RocketOutlined /> },
  { title: 'Connect SES', icon: <KeyOutlined /> },
  { title: 'Create List', icon: <UnorderedListOutlined /> },
  { title: 'Ready!',      icon: <CheckCircleOutlined /> },
];

const StepIndicator = ({ current }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32, gap: 0 }}>
    {STEPS.map((step, i) => {
      const done   = i < current;
      const active = i === current;
      return (
        <React.Fragment key={step.title}>
          {/* Node */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, flexShrink: 0,
              background: done
                ? 'var(--ok-bg)'
                : active
                  ? 'var(--brand-primary)'
                  : 'var(--surface-2)',
              color: done
                ? 'var(--ok)'
                : active
                  ? '#fff'
                  : 'var(--text-3)',
              border: done
                ? '2px solid var(--ok)'
                : active
                  ? '2px solid var(--brand-primary)'
                  : '2px solid var(--border)',
              transition: 'all 0.2s',
            }}>
              {done ? <CheckCircleOutlined style={{ fontSize: 17 }} /> : step.icon}
            </div>
            <span style={{
              fontSize: 11, marginTop: 6, fontWeight: active ? 600 : 400,
              color: active ? 'var(--text)' : done ? 'var(--ok)' : 'var(--text-3)',
              whiteSpace: 'nowrap',
            }}>
              {step.title}
            </span>
          </div>

          {/* Connector line */}
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, marginBottom: 20,
              background: i < current ? 'var(--ok)' : 'var(--border)',
              transition: 'background 0.3s',
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/* ── Step 0: Welcome + Profile Review ── */
const StepWelcome = ({ onNext }) => {
  const [form]    = Form.useForm();
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const payload      = getJwtPayload();
  const initialName  = payload.name  || localStorage.getItem('userName') || '';
  const initialOrg   = payload.org_name || localStorage.getItem('orgName') || '';
  const email        = payload.email || '';

  // Derive initials for the avatar
  const initials = (initialName || initialOrg || email)
    .split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

  const handleNext = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    setError('');
    try {
      const res = await axios.put('/api/auth/me', {
        name:          values.name.trim(),
        business_name: values.business_name.trim(),
      }, { headers: authHeader() });
      // Persist updated token + local name cache
      if (res.data.token) localStorage.setItem('token', res.data.token);
      localStorage.setItem('userName', values.name.trim());
      localStorage.setItem('orgName',  values.business_name.trim());
      onNext();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save profile — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{
          width: 72, height: 72,
          background: 'linear-gradient(135deg, var(--brand-primary), #2e6bf5)',
          borderRadius: '50%',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
          boxShadow: '0 8px 24px rgba(10,64,217,0.25)',
        }}>
          <RocketOutlined style={{ fontSize: 32, color: '#fff' }} />
        </div>
        <div style={{ fontWeight: 700, fontSize: 21, color: 'var(--text)', marginBottom: 6, letterSpacing: '-0.02em' }}>
          Welcome to SES Mailbox!
        </div>
        <Text style={{ color: 'var(--text-3)', fontSize: 14 }}>
          Let's confirm your details before we get started.
        </Text>
      </div>

      {/* Avatar + email row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)', padding: '12px 16px', marginBottom: 20,
      }}>
        <Avatar size={44} style={{ background: 'var(--brand-primary)', fontSize: 17, fontWeight: 700, flexShrink: 0 }}>
          {initials}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block' }}>
            {email}
          </Text>
          <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Account owner · Free plan
          </Text>
        </div>
        <EditOutlined style={{ marginLeft: 'auto', color: 'var(--text-3)', fontSize: 14, flexShrink: 0 }} />
      </div>

      {/* Editable fields */}
      <Form
        form={form}
        layout="vertical"
        size="large"
        initialValues={{ name: initialName, business_name: initialOrg }}
      >
        <Form.Item
          name="name"
          label="Your Full Name"
          rules={[{ required: true, message: 'Please enter your name' }]}
        >
          <Input
            prefix={<UserOutlined style={{ color: 'var(--text-3)' }} />}
            placeholder="Jane Smith"
            style={{ borderRadius: 'var(--r-md)' }}
          />
        </Form.Item>

        <Form.Item
          name="business_name"
          label="Organization / Business Name"
          rules={[{ required: true, message: 'Please enter your organization name' }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            prefix={<BankOutlined style={{ color: 'var(--text-3)' }} />}
            placeholder="Acme Corp"
            style={{ borderRadius: 'var(--r-md)' }}
          />
        </Form.Item>
      </Form>

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginTop: 12, borderRadius: 'var(--r-md)' }} />
      )}

      {/* Steps preview */}
      <div style={{
        display: 'flex', gap: 8, marginTop: 20, marginBottom: 24,
        padding: '12px 14px', background: 'var(--surface-2)',
        border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
      }}>
        {[
          { num: '1', text: 'Connect AWS SES' },
          { num: '2', text: 'Create a list' },
          { num: '3', text: 'Start sending' },
        ].map(({ num, text }, i) => (
          <React.Fragment key={num}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: 'var(--brand-tint)', color: 'var(--brand-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 11, border: '1px solid #0a40d933',
              }}>{num}</div>
              <Text style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</Text>
            </div>
            {i < 2 && <div style={{ width: 1, background: 'var(--border)', flexShrink: 0 }} />}
          </React.Fragment>
        ))}
      </div>

      <Button
        type="primary" size="large" block
        icon={<ArrowRightOutlined />}
        loading={saving}
        onClick={handleNext}
        style={{ borderRadius: 'var(--r-md)', height: 48, fontWeight: 600, fontSize: 15 }}
      >
        Looks good — Let's Go!
      </Button>
    </div>
  );
};

/* ── AWS SES Setup Guide ── */
const GuideStep = ({ num, icon, title, description, link, linkLabel }) => (
  <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
    <div style={{
      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
      background: 'var(--brand-tint)', border: '1.5px solid #0a40d933',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--brand-primary)', fontSize: 13, fontWeight: 700,
    }}>{num}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
        <span style={{ color: 'var(--brand-primary)', fontSize: 14 }}>{icon}</span>
        <Text style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{title}</Text>
      </div>
      <Text style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6, display: 'block' }}>{description}</Text>
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, color: 'var(--brand-primary)', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <LinkOutlined /> {linkLabel || 'Open in AWS Console'}
        </a>
      )}
    </div>
  </div>
);

const SESGuide = () => (
  <Collapse
    ghost
    style={{ marginTop: 16, marginBottom: 4 }}
    items={[{
      key: 'guide',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--brand-primary)', fontWeight: 600, fontSize: 13 }}>
          <QuestionCircleOutlined />
          How to get your AWS credentials — step-by-step guide
        </div>
      ),
      children: (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: '4px 16px 8px',
        }}>
          <GuideStep
            num="1"
            icon={<UserOutlined />}
            title="Create an IAM user with SES permissions"
            description={
              <>
                In the AWS Console go to <strong>IAM → Users → Create user</strong>. Give it a name like <code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>ses-mailbox-sender</code>. On the permissions step select <strong>Attach policies directly</strong> and search for <code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>AmazonSESFullAccess</code> — tick it and create the user.
              </>
            }
            link="https://console.aws.amazon.com/iam/home#/users"
            linkLabel="Open IAM Users →"
          />
          <GuideStep
            num="2"
            icon={<KeyOutlined />}
            title="Generate an access key"
            description={
              <>
                Open the user you just created, go to the <strong>Security credentials</strong> tab, scroll to <em>Access keys</em> and click <strong>Create access key</strong>. Choose <em>Application running outside AWS</em>. Copy both the <strong>Access Key ID</strong> and <strong>Secret Access Key</strong> — the secret is only shown once.
              </>
            }
            link="https://console.aws.amazon.com/iam/home#/users"
            linkLabel="Open IAM Users →"
          />
          <GuideStep
            num="3"
            icon={<GlobalOutlined />}
            title="Verify your sending email or domain"
            description={
              <>
                In the AWS Console go to <strong>SES → Verified identities → Create identity</strong>. You can verify a single email address (easiest, good for testing) or an entire domain (recommended for production — requires adding DNS records your host provides). Your <em>From Email</em> must belong to a verified identity.
              </>
            }
            link="https://console.aws.amazon.com/ses/home#/verified-identities"
            linkLabel="Open SES Verified Identities →"
          />
          <GuideStep
            num="4"
            icon={<SafetyOutlined />}
            title="Request production access (exit sandbox)"
            description={
              <>
                New AWS accounts start in the <strong>SES sandbox</strong> — you can only send to verified email addresses. To send to anyone, go to <strong>SES → Account dashboard → Request production access</strong> and submit the short form. AWS usually approves within 24 hours. Until then you can still test by verifying recipient addresses.
              </>
            }
            link="https://console.aws.amazon.com/ses/home#/account"
            linkLabel="Open SES Account Dashboard →"
          />
          <div style={{ paddingTop: 10, paddingBottom: 4 }}>
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message={<span style={{ fontSize: 12 }}>Choose the <strong>same AWS region</strong> below that you verified your identity in — SES identities are region-specific.</span>}
              style={{ borderRadius: 'var(--r-md)' }}
            />
          </div>
        </div>
      ),
    }]}
  />
);

/* ── Step 1: Connect SES ── */
const AWS_REGIONS = [
  { value: 'us-east-1',      label: 'US East (N. Virginia)' },
  { value: 'us-east-2',      label: 'US East (Ohio)' },
  { value: 'us-west-1',      label: 'US West (N. California)' },
  { value: 'us-west-2',      label: 'US West (Oregon)' },
  { value: 'eu-west-1',      label: 'EU (Ireland)' },
  { value: 'eu-west-2',      label: 'EU (London)' },
  { value: 'eu-central-1',   label: 'EU (Frankfurt)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-south-1',     label: 'Asia Pacific (Mumbai)' },
];

const StepSES = ({ onNext, onBack, onSkip }) => {
  const [form]    = Form.useForm();
  const [testing, setTesting]     = useState(false);
  const [saving,  setSaving]      = useState(false);
  const [sesVerified, setSesVerified] = useState(false);
  const [testResult,  setTestResult]  = useState(null);

  const handleTest = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }

    setTesting(true);
    setSesVerified(false);
    setTestResult(null);
    try {
      await axios.put('/api/settings/ses', {
        ses_access_key_id:     values.ses_access_key_id,
        ses_secret_access_key: values.ses_secret_access_key,
        ses_region:            values.ses_region,
        ses_from_email:        values.ses_from_email,
        ses_from_name:         values.ses_from_name,
        ses_send_rate:         14,
      }, { headers: authHeader() });

      const res = await axios.post('/api/settings/ses/test', {}, { headers: authHeader() });
      setSesVerified(true);
      setTestResult({
        type: 'success',
        msg: `Connected! Region: ${res.data.region} · From: ${res.data.fromEmail} · ${res.data.quota?.max24HourSend?.toLocaleString()} emails/day quota`,
      });
    } catch (err) {
      setTestResult({ type: 'error', msg: err.response?.data?.message || 'Connection failed — check your credentials' });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!sesVerified) return;
    setSaving(true);
    try {
      const values = form.getFieldsValue();
      await axios.put('/api/settings/ses', {
        ses_access_key_id:     values.ses_access_key_id,
        ses_secret_access_key: values.ses_secret_access_key,
        ses_region:            values.ses_region,
        ses_from_email:        values.ses_from_email,
        ses_from_name:         values.ses_from_name,
        ses_send_rate:         14,
      }, { headers: authHeader() });
      onNext();
    } catch (err) {
      setTestResult({ type: 'error', msg: err.response?.data?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 'var(--r-md)',
          background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <KeyOutlined style={{ fontSize: 20, color: '#fff' }} />
        </div>
        <div>
          <div style={{ fontWeight: 650, fontSize: 16, color: 'var(--text)' }}>Connect Your AWS SES Account</div>
          <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>
            Your credentials are stored encrypted. We never share a single SES account across customers.
          </Text>
        </div>
      </div>

      <SESGuide />

      <div style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: '20px 20px 4px',
        marginTop: 8,
      }}>
        <Form form={form} layout="vertical" size="middle" initialValues={{ ses_region: 'us-east-1' }}>
          <Form.Item
            name="ses_access_key_id"
            label="AWS Access Key ID"
            rules={[{ required: true, message: 'Access Key ID is required' }]}
          >
            <Input placeholder="AKIA…" style={{ borderRadius: 'var(--r-md)', fontFamily: 'var(--font-mono)' }} />
          </Form.Item>

          <Form.Item
            name="ses_secret_access_key"
            label="AWS Secret Access Key"
            rules={[{ required: true, message: 'Secret Access Key is required' }]}
          >
            <Input.Password placeholder="••••••••••••••••••••" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>

          <Form.Item
            name="ses_region"
            label="AWS Region"
            rules={[{ required: true, message: 'Select a region' }]}
          >
            <Select style={{ borderRadius: 'var(--r-md)' }}>
              {AWS_REGIONS.map(r => (
                <Option key={r.value} value={r.value}>{r.label} ({r.value})</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ses_from_email"
            label="From Email Address"
            rules={[{ required: true, type: 'email', message: 'Enter a valid verified email' }]}
          >
            <Input placeholder="noreply@yourdomain.com" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>

          <Form.Item
            name="ses_from_name"
            label="From Name"
            rules={[{ required: true, message: 'Enter a display name' }]}
          >
            <Input placeholder="Acme Newsletter" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
        </Form>
      </div>

      {testResult && (
        <Alert
          type={testResult.type}
          message={testResult.msg}
          showIcon
          style={{ marginTop: 12, borderRadius: 'var(--r-md)' }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, flexWrap: 'wrap', gap: 10 }}>
        <Button onClick={onBack} icon={<ArrowLeftOutlined />} style={{ borderRadius: 'var(--r-md)' }}>
          Back
        </Button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            type="text"
            onClick={onSkip}
            style={{ borderRadius: 'var(--r-md)', color: 'var(--text-3)', fontSize: 13 }}
          >
            Skip for now →
          </Button>
          <Button
            onClick={handleTest} loading={testing} icon={<KeyOutlined />}
            style={{ borderRadius: 'var(--r-md)' }}
          >
            Test Connection
          </Button>
          <Button
            type="primary" onClick={handleSaveAndNext} loading={saving}
            disabled={!sesVerified} icon={<ArrowRightOutlined />}
            style={{ borderRadius: 'var(--r-md)' }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ── Step 2: Create list ── */
const StepCreateList = ({ onNext, onBack, onSkip }) => {
  const [form]    = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleCreate = async (values) => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/lists', { name: values.list_name }, { headers: authHeader() });
      onNext();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create list');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 'var(--r-md)',
          background: 'linear-gradient(135deg, #059669, #047857)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <UnorderedListOutlined style={{ fontSize: 20, color: '#fff' }} />
        </div>
        <div>
          <div style={{ fontWeight: 650, fontSize: 16, color: 'var(--text)' }}>Create Your First Subscriber List</div>
          <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>
            Lists help you organise your audience. You can create more later.
          </Text>
        </div>
      </div>

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />
      )}

      <Form form={form} onFinish={handleCreate} layout="vertical" size="large">
        <Form.Item
          name="list_name"
          label="List Name"
          rules={[{ required: true, message: 'Give your list a name' }]}
        >
          <Input
            placeholder="e.g. Newsletter Subscribers, Customers, Leads…"
            style={{ borderRadius: 'var(--r-md)', height: 48 }}
            autoFocus
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 10 }}>
          <Button onClick={onBack} icon={<ArrowLeftOutlined />} style={{ borderRadius: 'var(--r-md)' }}>
            Back
          </Button>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Button
              type="text"
              onClick={onSkip}
              style={{ borderRadius: 'var(--r-md)', color: 'var(--text-3)', fontSize: 13 }}
            >
              Skip for now →
            </Button>
            <Button
              type="primary" htmlType="submit" loading={loading}
              icon={<ArrowRightOutlined />} style={{ borderRadius: 'var(--r-md)' }}
            >
              Create List
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

/* ── Step 3: Ready ── */
const StepReady = ({ onFinish }) => {
  const [loading, setLoading] = useState(false);

  const go = async (path) => {
    setLoading(true);
    await onFinish(path);
    setLoading(false);
  };

  return (
    <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
      <div style={{
        width: 80, height: 80,
        background: 'linear-gradient(135deg, #059669, #047857)',
        borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
        boxShadow: '0 8px 24px rgba(5,150,105,0.3)',
      }}>
        <CheckCircleOutlined style={{ fontSize: 40, color: '#fff' }} />
      </div>

      <div style={{ fontWeight: 700, fontSize: 22, color: 'var(--text)', marginBottom: 10, letterSpacing: '-0.02em' }}>
        You're all set!
      </div>
      <Paragraph style={{ color: 'var(--text-3)', maxWidth: 400, margin: '0 auto 32px', fontSize: 15 }}>
        Your SES account is connected and your first list is created.
        Time to send your first campaign!
      </Paragraph>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 280, margin: '0 auto' }}>
        <Button
          type="primary" size="large" loading={loading}
          icon={<RocketOutlined />} onClick={() => go('/campaigns/create')}
          style={{ borderRadius: 'var(--r-md)', height: 48, fontWeight: 600, fontSize: 15 }}
        >
          Create Your First Campaign
        </Button>
        <Button
          size="large" onClick={() => go('/')}
          style={{ borderRadius: 'var(--r-md)', height: 44 }}
        >
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
};

/* ── Main Onboarding wizard ── */
const Onboarding = () => {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const next = () => setCurrent(c => c + 1);
  const prev = () => setCurrent(c => c - 1);

  const finishOnboarding = async (destination) => {
    // Set the local flag FIRST so PrivateRoute won't redirect back even if
    // the API call below fails or returns before the token is updated.
    localStorage.setItem('_ob_done', '1');
    try {
      const res = await axios.post('/api/auth/complete-onboarding', {}, { headers: authHeader() });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.removeItem('_ob_done'); // clean up — fresh token already has onboarding_complete: true
      }
    } catch (err) {
      console.error('complete-onboarding error (non-fatal):', err?.response?.data || err.message);
      // _ob_done flag stays so PrivateRoute still lets the user through
    }
    navigate(destination);
  };

  const stepContent = [
    <StepWelcome    key="welcome" onNext={next} />,
    <StepSES        key="ses"     onNext={next} onBack={prev} onSkip={next} />,
    <StepCreateList key="list"    onNext={next} onBack={prev} onSkip={next} />,
    <StepReady      key="ready"   onFinish={finishOnboarding} />,
  ];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(150deg, var(--brand-ink) 0%, oklch(0.32 0.12 265) 55%, oklch(0.28 0.15 263) 100%)',
      padding: '24px 16px',
    }}>
      {/* Branding */}
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
          <MailOutlined style={{ fontSize: 18, color: '#fff' }} />
        </div>
        <Text style={{ color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>
          SES Mailbox
        </Text>
      </div>

      {/* White card */}
      <div style={{
        background: 'var(--surface)',
        borderRadius: 20,
        padding: '36px 40px',
        width: '100%',
        maxWidth: 580,
        boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        border: '1px solid var(--border)',
      }}>
        <StepIndicator current={current} />
        {stepContent[current]}
      </div>

      <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginTop: 24 }}>
        Step {current + 1} of {STEPS.length}
      </Text>
    </div>
  );
};

export default Onboarding;
